import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  DAYS, ALERT_AT, SUSTAIN_DAYS, INPUTS, SOURCES, SOURCE_INPUTS, CUSTOMERS,
  valueAt, scoreOf, contributions, band, labelFor, rng,
} from './simData';

const START_DAY = 28;       // history already played when the animation starts
const STATIC_DAY = 104;     // where a reduced-motion visitor sees the world
const SEC_PER_DAY = 0.5;
const TRAVEL = 0.95;        // seconds for an item to reach its entity

const BAND_COLOR = {
  healthy: '#4cc38a', low: '#a3d46a', moderate: '#ffd166', high: '#ff9b54', critical: '#ff5d6c',
};
const DAWN = '#ffc27a';
const TEAL = '#3fe0c5';

function newWorld(seed = 42) {
  const r = rng(seed);
  const custs = CUSTOMERS.map((c) => {
    const values = {};
    for (const inp of INPUTS) values[inp.key] = valueAt(c.inputs[inp.key], 0);
    return { ...c, values, score: scoreOf(values), hist: [], streak: 0, warned: false, alerted: false, alertDay: null, churned: false };
  });
  return {
    r, day: 0, lastDay: -1, custs, queue: [], particles: [], labels: [], beams: [],
    counts: { events: 0, signals: 0, review: 0, alerts: 0 }, feed: [], clock: 0,
  };
}

function pushFeed(w, item) {
  w.feed.unshift({ ...item, id: `${w.clock}-${w.feed.length}-${Math.floor(w.r() * 1e6)}` });
  if (w.feed.length > 6) w.feed.length = 6;
}

// Queue one simulated day's items for every customer, plus a few unmatched senders.
function planDay(w, d) {
  const { r } = w;
  for (const c of w.custs) {
    const gone = c.churnDay != null && d > c.churnDay;
    for (const s of SOURCES) {
      let p = c.volume[s.key] ?? 0;
      if (c.quietFrom && s.key === 'email' && d > c.quietFrom) p *= Math.max(0.35, 1 - (d - c.quietFrom) / 80);
      if (gone) p *= s.key === 'erp' ? 0 : 0.35;
      let n = r() < p ? 1 : 0;
      if (c.burst && d >= c.burst[0] && d <= c.burst[1] && (s.key === 'logs' || s.key === 'zendesk')) n += 2;
      for (let i = 0; i < n; i++) w.queue.push({ t: d + r(), src: s.key, cust: c.id });
    }
  }
  if (r() < 0.35) w.queue.push({ t: d + r(), src: r() < 0.6 ? 'email' : 'zendesk', cust: null });
  w.queue.sort((a, b) => a.t - b.t);
}

// An item arrives at its entity: the stage moves the inputs its source can move.
function arrive(w, ev, animate, geo) {
  w.counts.events++;
  if (!ev.cust) {
    w.counts.review++;
    return;
  }
  const c = w.custs.find((x) => x.id === ev.cust);
  const keys = SOURCE_INPUTS[ev.src];
  let best = null;
  let bestDelta = 0;
  for (const k of keys) {
    const target = Math.min(1, Math.max(0, valueAt(c.inputs[k], w.day) + (w.r() - 0.5) * 0.04));
    const delta = target - c.values[k];
    c.values[k] = target;
    w.counts.signals++;
    if (Math.abs(delta) > Math.abs(bestDelta)) { bestDelta = delta; best = k; }
  }
  c.score = scoreOf(c.values);
  if (!animate) return;
  let text = null;
  let color = DAWN;
  if (best && Math.abs(bestDelta) > 0.05) text = labelFor(best, bestDelta > 0);
  else if (ev.src === 'logs' && c.burst && w.day >= c.burst[0] && w.day <= c.burst[1]) { text = 'event: outage'; color = '#c3a6ff'; }
  if (text && geo) {
    const recent = w.labels.find((l) => l.cust === c.id && l.age < 0.7);
    if (!recent && !(geo.narrow && w.labels.length >= 1)) w.labels.push({ cust: c.id, text, color: bestDelta < 0 && best ? '#7fd8b0' : color, age: 0 });
  }
}

function endDay(w, d, animate) {
  for (const c of w.custs) {
    c.hist[d] = c.score;
    if (c.score >= ALERT_AT) {
      c.streak++;
      if (c.streak === SUSTAIN_DAYS && !c.alerted) {
        c.alerted = true;
        c.alertDay = d;
        w.counts.alerts++;
        pushFeed(w, { day: d, kind: 'alert', cust: c.short, text: `Trigger fired: ${c.name} risk ${c.score.toFixed(0)} held ≥ ${ALERT_AT} for ${SUSTAIN_DAYS} days` });
        pushFeed(w, { day: d, kind: 'action', cust: c.short, text: `Action: Teams alert routed to the ${c.bu} account owner · sent` });
        if (animate) w.beams.push({ cust: c.id, age: 0 });
      }
    } else {
      if (c.streak > 0 && c.streak < SUSTAIN_DAYS && !c.alerted && !c.warned) {
        c.warned = true;
        pushFeed(w, { day: d, kind: 'held', cust: c.short, text: `${c.name}: ≥ ${ALERT_AT} for ${c.streak} day${c.streak > 1 ? 's' : ''}, not sustained · no alert` });
      }
      c.streak = 0;
    }
    if (c.churnDay === d && !c.churned) {
      c.churned = true;
      const lead = c.alerted ? ` · alert led it by ${d - c.alertDay} days` : ' · no alert before it';
      pushFeed(w, { day: d, kind: 'label', cust: c.short, text: `${c.name} placed its last order (the churn label)${lead}` });
    }
  }
}

// Advance simulated time by `days`, spawning particles (animate) or applying items directly.
function advance(w, days, animate, geo) {
  const target = Math.min(DAYS, w.day + days);
  while (w.day < target) {
    const nextBoundary = Math.floor(w.day) + 1;
    const stepTo = Math.min(target, nextBoundary);
    if (Math.floor(w.day) > w.lastDay) { w.lastDay = Math.floor(w.day); planDay(w, w.lastDay); }
    while (w.queue.length && w.queue[0].t < stepTo) {
      const ev = w.queue.shift();
      w.day = Math.max(w.day, ev.t);
      if (animate && geo) spawnParticle(w, ev, geo);
      else arrive(w, ev, false);
    }
    w.day = stepTo;
    if (stepTo === nextBoundary) endDay(w, nextBoundary - 1, animate);
  }
}

function spawnParticle(w, ev, geo) {
  const si = SOURCES.findIndex((s) => s.key === ev.src);
  const from = geo.src[si];
  const to = ev.cust ? geo.ent[w.custs.findIndex((c) => c.id === ev.cust)] : geo.review;
  const jitter = (w.r() - 0.5) * 16;
  w.particles.push({ ev, from, to, jitter, t: 0, color: SOURCES[si].color });
}

function layout(W, H) {
  const narrow = W < 640;
  const srcX = narrow ? 70 : 128;
  const gateX = narrow ? W * 0.36 : W * 0.34;
  const entX = narrow ? W * 0.56 : W * 0.6;
  const trigX = W * 0.86;
  const top = 44;
  const srcs = SOURCES.map((_, i) => ({ x: srcX, y: top + 10 + (i * (H - top - 60)) / (SOURCES.length - 1) }));
  const ents = CUSTOMERS.map((_, i) => ({ x: entX, y: top + 18 + (i * (H - top - 92)) / (CUSTOMERS.length - 1) }));
  return {
    W, H, narrow, srcX, gateX, entX, trigX, top,
    src: srcs, ent: ents, review: { x: gateX, y: H - 20 },
    trig: { x: trigX, y: H * 0.34 }, act: { x: trigX, y: H * 0.66 },
  };
}

function bez(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

function draw(ctx, w, g, now) {
  const { W, H, narrow } = g;
  ctx.clearRect(0, 0, W, H);
  ctx.font = '600 10px ui-sans-serif, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(160,174,196,0.7)';
  ctx.textAlign = 'center';
  const heads = narrow
    ? [[g.srcX - 22, 'SOURCES'], [g.gateX, 'RESOLVE'], [g.entX + 34, 'ENTITIES']]
    : [[g.srcX - 40, 'SOURCES'], [g.gateX, 'ENTITY RESOLUTION'], [g.entX + 50, 'ENTITIES · SIGNALS · SCORE'], [g.trigX, 'TRIGGER · ACTION']];
  for (const [x, t] of heads) ctx.fillText(t, x, 18);

  // resolution gate
  ctx.strokeStyle = 'rgba(141,162,255,0.25)';
  ctx.setLineDash([3, 5]);
  ctx.beginPath(); ctx.moveTo(g.gateX, g.top - 6); ctx.lineTo(g.gateX, H - 40); ctx.stroke();
  ctx.setLineDash([]);

  // guide paths: sources funnel into resolution, resolution fans out to entities
  ctx.lineWidth = 1;
  SOURCES.forEach((s, i) => {
    const p = g.src[i];
    ctx.strokeStyle = hexA(s.color, 0.10);
    ctx.beginPath(); ctx.moveTo(p.x + 8, p.y);
    ctx.bezierCurveTo((p.x + g.gateX) / 2, p.y, (p.x + g.gateX) / 2, H / 2, g.gateX, H / 2);
    ctx.stroke();
  });
  g.ent.forEach((e) => {
    ctx.strokeStyle = 'rgba(141,162,255,0.10)';
    ctx.beginPath(); ctx.moveTo(g.gateX, H / 2);
    ctx.bezierCurveTo((g.gateX + e.x) / 2, H / 2, (g.gateX + e.x) / 2, e.y, e.x - 22, e.y);
    ctx.stroke();
  });
  ctx.strokeStyle = 'rgba(141,162,255,0.10)';
  ctx.beginPath(); ctx.moveTo(g.gateX, H / 2); ctx.lineTo(g.review.x, g.review.y - 12); ctx.stroke();

  // review queue
  const rq = `review queue ${w.counts.review}`;
  ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace';
  const rw = ctx.measureText(rq).width + 16;
  ctx.fillStyle = 'rgba(141,162,255,0.10)';
  ctx.strokeStyle = 'rgba(141,162,255,0.35)';
  roundRect(ctx, g.review.x - rw / 2, g.review.y - 10, rw, 20, 10); ctx.fill(); ctx.stroke();
  ctx.fillStyle = '#b8c4ff'; ctx.textAlign = 'center'; ctx.fillText(rq, g.review.x, g.review.y + 3.5);

  // sources
  SOURCES.forEach((s, i) => {
    const p = g.src[i];
    ctx.fillStyle = s.color;
    ctx.beginPath(); ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2); ctx.fill();
    ctx.textAlign = 'right';
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = '#e6ecf5';
    ctx.fillText(narrow ? s.label.split(' ')[0] : s.label, p.x - 10, p.y + (narrow ? 4 : 0));
    if (!narrow) {
      ctx.font = '400 10px ui-sans-serif, system-ui, sans-serif';
      ctx.fillStyle = 'rgba(160,174,196,0.75)';
      ctx.fillText(s.sub, p.x - 12, p.y + 13);
    }
  });

  // trigger + action nodes
  if (!narrow) {
    const active = w.beams.length > 0;
    ctx.save();
    ctx.translate(g.trig.x, g.trig.y); ctx.rotate(Math.PI / 4);
    ctx.fillStyle = active ? 'rgba(255,194,122,0.22)' : 'rgba(255,255,255,0.03)';
    ctx.strokeStyle = active ? DAWN : 'rgba(255,194,122,0.4)';
    ctx.lineWidth = 1.5;
    ctx.fillRect(-12, -12, 24, 24); ctx.strokeRect(-12, -12, 24, 24);
    ctx.restore();
    ctx.textAlign = 'center';
    ctx.font = '600 11px ui-sans-serif, system-ui, sans-serif'; ctx.fillStyle = '#e6ecf5';
    ctx.fillText('Trigger', g.trig.x, g.trig.y + 34);
    ctx.font = '400 10px ui-monospace, SFMono-Regular, Menlo, monospace'; ctx.fillStyle = 'rgba(160,174,196,0.8)';
    ctx.fillText(`risk ≥ ${ALERT_AT} for ${SUSTAIN_DAYS} days`, g.trig.x, g.trig.y + 48);
    ctx.fillStyle = active ? 'rgba(63,224,197,0.18)' : 'rgba(255,255,255,0.03)';
    ctx.strokeStyle = active ? TEAL : 'rgba(63,224,197,0.4)';
    roundRect(ctx, g.act.x - 46, g.act.y - 14, 92, 28, 6); ctx.fill(); ctx.stroke();
    ctx.font = '600 11px ui-sans-serif, system-ui, sans-serif'; ctx.fillStyle = '#e6ecf5';
    ctx.fillText('Action', g.act.x, g.act.y + 4);
    ctx.font = '400 10px ui-sans-serif, system-ui, sans-serif'; ctx.fillStyle = 'rgba(160,174,196,0.8)';
    ctx.fillText('Teams · email · webhook', g.act.x, g.act.y + 30);
    ctx.fillText(`${w.counts.alerts} fired`, g.act.x, g.act.y + 44);
  }

  // beams (alert → trigger → action)
  for (const b of w.beams) {
    const i = w.custs.findIndex((c) => c.id === b.cust);
    const e = g.ent[i];
    const k = Math.min(1, b.age / 0.8);
    const fade = b.age > 2.2 ? Math.max(0, 1 - (b.age - 2.2) / 0.8) : 1;
    ctx.strokeStyle = `rgba(255,194,122,${0.85 * fade})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (narrow) {
      ctx.arc(e.x, e.y, 26 + k * 10, 0, Math.PI * 2);
    } else {
      ctx.moveTo(e.x + 20, e.y);
      const k1 = Math.min(1, k * 2);
      ctx.lineTo(e.x + 20 + (g.trig.x - 16 - e.x - 20) * k1, e.y + (g.trig.y - e.y) * k1);
      if (k > 0.5) {
        const k2 = (k - 0.5) * 2;
        ctx.moveTo(g.trig.x, g.trig.y + 16);
        ctx.lineTo(g.act.x, g.trig.y + 16 + (g.act.y - 14 - g.trig.y - 16) * k2);
      }
    }
    ctx.stroke();
    ctx.lineWidth = 1;
  }

  // particles
  for (const p of w.particles) {
    ctx.fillStyle = p.color;
    for (let k = 5; k >= 0; k--) {
      const t = easeInOut(Math.max(0, Math.min(1, (p.t - k * 0.035) / TRAVEL)));
      const x = bez(p.from.x, g.gateX, g.gateX, p.to.x, t);
      const y = bez(p.from.y, p.from.y + p.jitter, p.to.y + p.jitter, p.to.y, t);
      ctx.globalAlpha = k === 0 ? 1 : 0.35 * (1 - k / 6);
      ctx.beginPath(); ctx.arc(x, y, k === 0 ? 3.2 : 2.4, 0, Math.PI * 2); ctx.fill();
      if (k === 0) { ctx.globalAlpha = 0.2; ctx.beginPath(); ctx.arc(x, y, 8, 0, Math.PI * 2); ctx.fill(); }
    }
    ctx.globalAlpha = 1;
  }

  // entities
  w.custs.forEach((c, i) => {
    const e = g.ent[i];
    const bcol = BAND_COLOR[band(c.score)];
    ctx.fillStyle = '#0c1220';
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(e.x, e.y, 17, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = bcol;
    ctx.beginPath(); ctx.arc(e.x, e.y, 17, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * Math.min(100, c.score)) / 100); ctx.stroke();
    ctx.lineWidth = 1;
    if (c.alerted) {
      const pulse = (Math.sin(now / 300) + 1) / 2;
      ctx.strokeStyle = `rgba(255,194,122,${0.25 + 0.35 * pulse})`;
      ctx.beginPath(); ctx.arc(e.x, e.y, 23 + pulse * 2, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.textAlign = 'center';
    ctx.font = '700 11px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.fillStyle = '#e6ecf5';
    ctx.fillText(c.score.toFixed(0), e.x, e.y + 4);
    ctx.textAlign = 'left';
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
    ctx.fillText(narrow ? c.short : c.name, e.x + 28, e.y - 2);
    ctx.font = '400 10px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = bcol;
    ctx.fillText(band(c.score) + (c.churned ? ' · churned' : ''), e.x + 28, e.y + 12);
  });

  // floating signal labels
  ctx.font = '600 11px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.textAlign = 'left';
  for (const l of w.labels) {
    const i = w.custs.findIndex((c) => c.id === l.cust);
    const e = g.ent[i];
    const a = l.age < 0.2 ? l.age / 0.2 : Math.max(0, 1 - (l.age - 0.9) / 0.8);
    ctx.globalAlpha = a;
    const x = e.x + 22;
    const y = e.y - 26 - l.age * 10;
    const tw = ctx.measureText(l.text).width;
    ctx.fillStyle = 'rgba(8,12,20,0.85)';
    roundRect(ctx, x - 6, y - 12, tw + 12, 18, 9); ctx.fill();
    ctx.fillStyle = l.color;
    ctx.fillText(l.text, x, y + 1);
    ctx.globalAlpha = 1;
  }
}

function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function snapshot(w) {
  return {
    day: Math.min(DAYS, Math.floor(w.day)),
    counts: { ...w.counts },
    feed: w.feed.slice(),
    custs: w.custs.map((c) => ({
      id: c.id, name: c.name, short: c.short, bu: c.bu, archetype: c.archetype, score: c.score,
      band: band(c.score), hist: c.hist.slice(), alerted: c.alerted, alertDay: c.alertDay,
      churned: c.churned, churnDay: c.churnDay, contribs: contributions(c.values),
    })),
  };
}

function Spark({ hist, alertDay, churnDay, day }) {
  const W = 300; const H = 70;
  const x = (d) => (d / (DAYS - 1)) * W;
  const y = (v) => H - (v / 100) * H;
  const pts = hist.map((v, d) => (v == null ? null : `${x(d).toFixed(1)},${y(v).toFixed(1)}`)).filter(Boolean);
  return (
    <svg className="kz-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
      aria-label={`Risk score over simulated days 0 to ${day}`}>
      <rect x="0" y={y(100)} width={W} height={y(ALERT_AT) - y(100)} className="kz-spark-band" />
      <line x1="0" x2={W} y1={y(ALERT_AT)} y2={y(ALERT_AT)} className="kz-spark-thresh" />
      {churnDay != null && <line x1={x(churnDay)} x2={x(churnDay)} y1="0" y2={H} className="kz-spark-churn" />}
      {pts.length > 1 && <polyline points={pts.join(' ')} className="kz-spark-line" fill="none" />}
      {alertDay != null && hist[alertDay] != null && (
        <circle cx={x(alertDay)} cy={y(hist[alertDay])} r="4" className="kz-spark-alert" />
      )}
    </svg>
  );
}

const KIND_LABEL = { alert: 'trigger', action: 'action', held: 'held', label: 'label' };

export default function SignalSim() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const worldRef = useRef(null);
  const geoRef = useRef(null);
  const [snap, setSnap] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [pinned, setPinned] = useState(null);
  const playingRef = useRef(true);
  const visibleRef = useRef(true);

  const ready = snap != null;
  const reset = useCallback((toDay) => {
    const w = newWorld(42);
    advance(w, toDay, false, null);
    worldRef.current = w;
    setSnap(snapshot(w));
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    reset(reduce ? STATIC_DAY : START_DAY);
    if (reduce) { playingRef.current = false; setPlaying(false); }
  }, [reset]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');
    let raf = 0; let last = performance.now(); let lastSnap = 0;

    const resize = () => {
      const W = wrap.clientWidth; const H = wrap.clientHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      geoRef.current = layout(W, H);
      if (worldRef.current) draw(ctx, worldRef.current, geoRef.current, performance.now());
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(wrap);
    const io = new IntersectionObserver(([e]) => { visibleRef.current = e.isIntersecting; }, { threshold: 0.05 });
    io.observe(wrap);

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const w = worldRef.current; const g = geoRef.current;
      if (!w || !g || !visibleRef.current || document.hidden) return;
      if (playingRef.current) {
        w.clock += dt;
        if (w.day >= DAYS && !w.particles.length) {
          w.endHold = (w.endHold || 0) + dt;
          if (w.endHold > 3) { reset(START_DAY); return; }
        } else {
          advance(w, dt / SEC_PER_DAY, true, g);
        }
        for (const p of w.particles) {
          p.t += dt;
          if (p.t >= TRAVEL) arrive(w, p.ev, true, g);
        }
        w.particles = w.particles.filter((p) => p.t < TRAVEL);
        for (const l of w.labels) l.age += dt;
        w.labels = w.labels.filter((l) => l.age < 1.7);
        for (const b of w.beams) b.age += dt;
        w.beams = w.beams.filter((b) => b.age < 3);
        if (now - lastSnap > 180) { lastSnap = now; setSnap(snapshot(w)); }
      }
      draw(ctx, w, g, now);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [reset, ready]);

  const toggle = () => { playingRef.current = !playingRef.current; setPlaying(playingRef.current); };
  const restart = () => { reset(START_DAY); playingRef.current = true; setPlaying(true); };

  if (!snap) return <div className="kz-sim" />;
  const auto = snap.custs.reduce((a, b) => (b.score > a.score ? b : a));
  const focus = snap.custs.find((c) => c.id === pinned) || auto;
  const top = focus.contribs.slice(0, 5);

  return (
    <div className="kz-sim">
      <div className="kz-sim-bar">
        <span className="kz-sim-tag">Simulation</span>
        <span className="kz-sim-day">day <b>{snap.day}</b> / {DAYS}</span>
        <span className="kz-sim-stat"><b>{snap.counts.events}</b> items</span>
        <span className="kz-sim-stat"><b>{snap.counts.signals}</b> signals</span>
        <span className="kz-sim-stat"><b>{snap.counts.alerts}</b> alerts</span>
        <span className="kz-sim-controls">
          <button type="button" className="kz-sim-btn" onClick={toggle} aria-pressed={!playing}>
            <i className={`fas ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" /> {playing ? 'Pause' : 'Play'}
          </button>
          <button type="button" className="kz-sim-btn" onClick={restart}>
            <i className="fas fa-rotate-left" aria-hidden="true" /> Restart
          </button>
        </span>
      </div>
      <div className="kz-sim-body">
        <div className="kz-sim-stage" ref={wrapRef}>
          <canvas ref={canvasRef} aria-hidden="true" />
        </div>
        <aside className="kz-sim-panel" aria-label="Customer health for the selected simulated customer">
          <div className="kz-sim-tabs" role="group" aria-label="Choose a simulated customer">
            {snap.custs.map((c) => (
              <button type="button" key={c.id} onClick={() => setPinned(pinned === c.id ? null : c.id)}
                className={`kz-sim-tab ${focus.id === c.id ? 'is-on' : ''}`} aria-pressed={pinned === c.id}>
                <span className={`kz-dot band-${c.band}`} aria-hidden="true" />{c.short}
              </button>
            ))}
          </div>
          <div className="kz-sim-head">
            <div>
              <div className="kz-sim-name">{focus.name}</div>
              <div className="kz-sim-meta"><code>{focus.id}</code> · BU {focus.bu} · {focus.archetype}</div>
            </div>
            <div className="kz-sim-score">
              <span className="kz-sim-score-n">{focus.score.toFixed(1)}</span>
              <span className={`kz-band band-${focus.band}`}>{focus.band}</span>
            </div>
          </div>
          <Spark hist={focus.hist} alertDay={focus.alertDay} churnDay={focus.churnDay} day={snap.day} />
          <div className="kz-spark-legend">
            <span><i className="lg lg-line" />risk (0–100, higher is worse)</span>
            <span><i className="lg lg-thresh" />alert band ≥ {ALERT_AT}</span>
            {focus.churnDay != null && <span><i className="lg lg-churn" />last order</span>}
          </div>
          <div className="kz-contrib-title">Why: contributions to the score</div>
          <ul className="kz-contribs">
            {top.map((c) => (
              <li key={c.key}>
                <code>{c.key}</code>
                <span className="kz-contrib-bar"><span style={{ width: `${Math.min(100, (c.points / 18) * 100)}%` }} /></span>
                <span className="kz-contrib-n">{c.points.toFixed(1)}</span>
              </li>
            ))}
          </ul>
          <div className="kz-feed-title">Triggers and actions</div>
          <ol className="kz-feed">
            {snap.feed.length === 0 && <li className="kz-feed-empty">Nothing has fired yet.</li>}
            {snap.feed.slice(0, 4).map((f) => (
              <li key={f.id} className={`kind-${f.kind}`}>
                <span className="kz-feed-day">d{f.day}</span>
                <span className="kz-feed-kind">{KIND_LABEL[f.kind]}</span>
                <span className="kz-feed-text">{f.text}</span>
              </li>
            ))}
          </ol>
        </aside>
      </div>
      <p className="kz-sim-note">
        A synthetic world, not customer data: four made-up accounts shaped like the scenario of Kizashi&rsquo;s
        source simulator (an overt churner, a silent churner, a decoy with a one-off outage, a healthy account).
        Signal names are real Yochō signals; the weights and the alert rule here are illustrative.
      </p>
    </div>
  );
}
